import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { X, Upload, Download, Send, CheckCircle, Calendar, FileText, User, Building, Calculator } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from "@/hooks/use-toast";
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency } from '@/utils/currency';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { StatusBadge } from '@/components/status/StatusBadge';
import type { PaymentRecord } from '../../types/payment.types';

export interface PaymentDetailDrawerProps {
  payment: PaymentRecord | any;
  onClose: () => void;
}

export const PaymentDetailDrawer: React.FC<PaymentDetailDrawerProps> = ({ payment, onClose }) => {
  const { userCountry } = useAuth();
  const [notes, setNotes] = useState('');
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);

  const paymentHistory = [
    { date: '2024-06-15', action: 'Payment initiated', amount: payment.netPayable, user: 'Finance Team' },
    { date: '2024-06-10', action: 'Invoice submitted', amount: null, user: payment.influencer?.name || 'Creator' },
    { date: '2024-06-08', action: 'Campaign completed', amount: null, user: 'Campaign Manager' }
  ];

  const handleUploadInvoice = () => {
    setUploadingInvoice(true);
    setTimeout(() => {
      setUploadingInvoice(false);
    }, 2000);
  };

  const handleMarkPaid = async () => {
    try {
      setMarkingPaid(true);

      const { error } = await supabase
        .from('payments')
        .update({
          status: 'paid',
          payment_date: new Date().toISOString().split('T')[0],
          updated_at: new Date().toISOString()
        })
        .eq('id', payment.id);

      if (error) throw error;

      toast({
        title: "Payment marked as Paid successfully",
        description: "The payment status has been updated.",
        variant: "default"
      });

      onClose();
    } catch (error) {
      console.error('Error marking payment as paid:', error);
      toast({
        title: "Error updating payment",
        description: "Failed to mark payment as paid. Please try again.",
        variant: "destructive"
      });
    } finally {
      setMarkingPaid(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Payment Details</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* Influencer & Campaign Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center space-x-4 mb-4">
              <Avatar className="w-12 h-12">
                <AvatarImage
                  src={payment.influencer?.profilePic || undefined}
                  alt={payment.influencer?.name || 'Creator'}
                  className="object-cover"
                />
                <AvatarFallback className="bg-gradient-to-r from-purple-500 to-blue-500 text-white font-semibold text-base">
                  {payment.influencer?.avatar || 'CR'}
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 className="font-semibold text-lg">{payment.influencer?.name || 'Unknown Influencer'}</h3>
                <p className="text-gray-600">{payment.campaign || 'Campaign'}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4 text-gray-500" />
                <span>@{payment.influencer?.name?.toLowerCase().replace(/\s+/g, '') || 'creator'}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Building className="w-4 h-4 text-gray-500" />
                <span>Instagram • 125K followers</span>
              </div>
            </div>
          </div>

          {/* Fee Breakdown */}
          <div className="bg-white border rounded-lg p-4">
            <h4 className="font-semibold mb-4 flex items-center">
              <Calculator className="w-5 h-5 mr-2 text-green-600" />
              Payment Breakdown
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span>Base Fee</span>
                <span>{formatCurrency(payment.baseFee || payment.fee || 0, userCountry)}</span>
              </div>
              {payment.gstApplied && (
                <div className="flex justify-between text-green-700">
                  <span>+ GST (18%)</span>
                  <span>{formatCurrency(payment.gstAmount || 0, userCountry)}</span>
                </div>
              )}
              {payment.tdsDeducted && (
                <div className="flex justify-between text-red-700">
                  <span>- TDS (10%)</span>
                  <span>{formatCurrency(payment.tdsAmount || 0, userCountry)}</span>
                </div>
              )}
              <hr />
              <div className="flex justify-between font-semibold text-lg">
                <span>Net Payable</span>
                <span>{formatCurrency(payment.netPayable || payment.fee || 0, userCountry)}</span>
              </div>
            </div>
          </div>

          {/* Status & Due Date */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white border rounded-lg p-4">
              <h4 className="font-semibold mb-2">Payment Status</h4>
              <StatusBadge status={payment.status} />
            </div>
            <div className="bg-white border rounded-lg p-4">
              <h4 className="font-semibold mb-2">Due Date</h4>
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span className={payment.status === 'overdue' ? 'text-red-600 font-medium' : ''}>
                  {payment.dueDate ? new Date(payment.dueDate).toLocaleDateString() : '-'}
                </span>
              </div>
            </div>
          </div>

          {/* Invoice Section */}
          <div className="bg-white border rounded-lg p-4">
            <h4 className="font-semibold mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-blue-600" />
              Invoice & Documents
            </h4>
            {payment.hasInvoice ? (
              <div className="flex items-center justify-between p-3 bg-green-50 rounded border border-green-200">
                <div className="flex items-center space-x-3">
                  <FileText className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium">Invoice_Campaign_Mamaearth.pdf</p>
                    <p className="text-sm text-gray-600">Uploaded on {payment.createdAt ? new Date(payment.createdAt).toLocaleDateString() : 'Recent'}</p>
                  </div>
                </div>
                <Button variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </Button>
              </div>
            ) : (
              <div className="text-center py-6">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-4">No invoice uploaded yet</p>
                <Button onClick={handleUploadInvoice} disabled={uploadingInvoice}>
                  <Upload className="w-4 h-4 mr-2" />
                  {uploadingInvoice ? 'Uploading...' : 'Upload Invoice'}
                </Button>
              </div>
            )}
          </div>

          {/* Payment History */}
          <div className="bg-white border rounded-lg p-4">
            <h4 className="font-semibold mb-4">Payment History</h4>
            <div className="space-y-4">
              {paymentHistory.map((entry, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="font-medium">{entry.action}</p>
                      {entry.amount && (
                        <span className="text-green-600 font-medium">{formatCurrency(entry.amount, userCountry)}</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <span>{entry.user}</span>
                      <span>{new Date(entry.date).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes Section */}
          <div className="bg-white border rounded-lg p-4">
            <h4 className="font-semibold mb-4">Internal Notes</h4>
            <Textarea
              placeholder="Add internal notes about this payment..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mb-3"
            />
            <Button size="sm">Save Notes</Button>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            {payment.status !== 'paid' && (
              <>
                <Button
                  onClick={handleMarkPaid}
                  disabled={markingPaid}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {markingPaid ? 'Updating...' : 'Mark as Paid'}
                </Button>
                <Button variant="outline">
                  <Send className="w-4 h-4 mr-2" />
                  Send Reminder
                </Button>
              </>
            )}
            <Button variant="outline">
              <FileText className="w-4 h-4 mr-2" />
              Generate Receipt
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetailDrawer;
